import { Inject, Injectable, OnDestroy } from '@angular/core';
import { InitializableInterface } from '../../interfaces/initializable.interface';
import {
    catchError,
    combineLatest,
    debounceTime,
    exhaustMap,
    from,
    fromEvent,
    map,
    merge,
    mergeMap,
    Observable,
    of,
    ReplaySubject,
    Subject,
    switchMap,
    takeUntil,
} from 'rxjs';
import { LocalStorageCommonService } from '../local-storage/local-storage-common.service';
import { isDefined } from '../../utilities/is-defined';
import { getUuidV4 } from '../../utilities/generic-utilities';
import { SessionId } from './session.type';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import { UriUtilitiesService } from '../uri-utilities/uri-utilities.service';
import { OnlineOnlyService } from '../online-only/online-only.service';
import { OdataAppVersionPayload } from '../app-version/odata-appversion-payload.interface';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { AppNotificationService } from '../app-notification/app-notification.service';
import { AppNotificationType } from '../app-notification/app-notification.model';
import { AppVersionAndSessionService } from '../app-version/app-version-and-session.service';
import { SynchronizationInstanceToken, SynchronizationInterface } from '../../interfaces/synchronization.interface';

// for easier debugging, change the following values: assumeSessionValid to 'false' and timeoutInSeconds to eg. 5
const assumeSessionValid = true;
const timeoutInSeconds = 300;

const SESSION_STORAGE_KEY = 'sessionId';
const msPerSecond = 1000;

@Injectable({
    providedIn: 'root',
})
export class SessionService implements InitializableInterface, OnDestroy {
    public isInitialized = false;

    private sessionId: SessionId;

    private destroy$ = new Subject<void>();
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly localStorageCommonService: LocalStorageCommonService,
        private readonly onlineOnlyService: OnlineOnlyService,
        private readonly uriUtilities: UriUtilitiesService,
        private readonly appNotificationService: AppNotificationService,
        private readonly appVersionAndSessionService: AppVersionAndSessionService,
        @Inject(SynchronizationInstanceToken)
        protected readonly synchronizationService: SynchronizationInterface,
    ) {}

    public ngOnDestroy(): void {
        this.destroy$.next();
    }

    public init(): Observable<SessionId> {
        return from(this.localStorageCommonService.storageGetAsync<SessionId>(SESSION_STORAGE_KEY)).pipe(
            switchMap((sessionId) => {
                const obs: Array<Observable<unknown>> = [];
                if (!isDefined(sessionId)) {
                    sessionId = getUuidV4();
                    obs.push(
                        from(
                            this.localStorageCommonService
                                .storageSetAsync(SESSION_STORAGE_KEY, sessionId)
                                .catch((error) =>
                                    this.log.error(this.constructor.name, 'failed to store session id', error),
                                ),
                        ),
                    );
                }

                this.sessionId = sessionId;
                this.isInitialized = true;

                return combineLatest([this.compareSessionAndAlertUser(this.sessionId), ...obs]);
            }),
            map(([sessionId]) => sessionId),
        );
    }

    public startSessionChecking(): void {
        if (!this.isInitialized) {
            throw new Error(`SessionService: startSessionChecking: Called before service init!`);
        }

        merge(
            fromEvent(window, 'mousemove'),
            fromEvent(window, 'click'),
            fromEvent(window, 'touchmove'),
            fromEvent(window, 'touchstart'),
        )
            .pipe(
                // only react to events after the given delay
                debounceTime(timeoutInSeconds * msPerSecond),

                // if delay passed without further events, then check with GW if we still have the valid session
                exhaustMap(() => this.compareSessionAndAlertUser(this.sessionId)),

                takeUntil(this.destroy$),
            )
            .subscribe();
    }

    /**
     * Returns true if the local session id is the same as the one maintained on GW.
     * If we are offline, we assume that we still are the correct session.
     *
     * @param sessionId
     */
    private compareSessionIdWithGw(sessionId: SessionId): Observable<boolean> {
        let query = ENDPOINTS.AppVersionSet.toString();
        const format = '$format=json';
        query = this.uriUtilities.appendParameter(query, format);

        return this.onlineOnlyService.get<OdataAppVersionPayload>(query).pipe(
            map((response) => (Array.isArray(response) ? response[0] : response)),
            map((response) => response.session_id === sessionId),
            catchError((err) => {
                this.log.warn(
                    `SessionService: compareSessionIdWithGw: Could not compare session with GW, assuming our session '${sessionId}' is still valid. Error: ${JSON.stringify(err)}`,
                );
                return of(assumeSessionValid);
            }),
        );
    }

    private compareSessionAndAlertUser(sessionId: string): Observable<string> {
        return this.compareSessionIdWithGw(sessionId).pipe(
            exhaustMap((isSameSession) => {
                if (isSameSession) {
                    return of(sessionId);
                }
                // if not the same session, then we inform the user and ask them if they know what they are doing

                // using ReplaySubject so that tests work ;)
                const trigger = new ReplaySubject<undefined>();

                const updateSessionAndSyncDataCallback$ = trigger.asObservable().pipe(
                    mergeMap(() => {
                        return this.appVersionAndSessionService.sendAppVersionAndSessionId(this.sessionId);
                    }),
                    mergeMap(() => {
                        return this.synchronizationService.receiveEverything();
                    }),
                    map(() => this.sessionId),
                    catchError((err) => {
                        this.log.error(
                            `SessionService: startSessionChecking: User confirmed to continue with session '${this.sessionId}', but there was an error setting it on GW: ${JSON.stringify(err)}`,
                        );
                        return of(this.sessionId);
                    }),
                );
                this.appNotificationService.setAppNotification(
                    AppNotificationType.WARN,
                    'Warning: Session changed!',
                    'PACE detected that you used it on a different device before. Do you really want to continue here? If yes, please ensure that all data has been submitted on the other device.',
                    () => {
                        trigger.next(undefined);
                    },
                );
                return updateSessionAndSyncDataCallback$;
            }),
        );
    }
}
